import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  DocumentTextIcon,
  PlusCircleIcon,
  RocketLaunchIcon,
  CalendarIcon,
  ChartBarIcon,
  SparklesIcon,
  ClockIcon,
  CheckCircleIcon,
  TagIcon
} from '@heroicons/react/24/outline';

const BlogAutomation = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('generate');
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [platforms, setPlatforms] = useState([]);

  // Form states
  const [topic, setTopic] = useState('');
  const [contentType, setContentType] = useState('tutorial');
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState('');

  // Generated content
  const [generatedPost, setGeneratedPost] = useState(null);
  const [contentIdeas, setContentIdeas] = useState([]);

  // Content ideas form
  const [industry, setIndustry] = useState('');
  const [targetAudience, setTargetAudience] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [analyticsRes, platformsRes] = await Promise.all([
        api.get('/blog-automation/content-analytics'),
        api.get('/blog-automation/publishing-platforms')
      ]);

      if (analyticsRes.data.success) {
        setAnalytics(analyticsRes.data.analytics);
      }

      if (platformsRes.data.success) {
        setPlatforms(platformsRes.data.supported_platforms);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleAddKeyword = (e) => {
    if (e.key === 'Enter' && keywordInput.trim()) {
      e.preventDefault();
      if (!keywords.includes(keywordInput.trim())) {
        setKeywords([...keywords, keywordInput.trim()]);
        setKeywordInput('');
      }
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setKeywords(keywords.filter(k => k !== keyword));
  };

  const handleGenerateBlogPost = async () => {
    if (!topic || keywords.length === 0) {
      alert('Please enter a topic and at least one keyword');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/blog-automation/generate-blog-post', {
        topic,
        content_type: contentType,
        target_keywords: keywords
      });

      if (response.data.success) {
        setGeneratedPost(response.data.blog_post);
      }
    } catch (error) {
      console.error('Error generating blog post:', error);
      alert(error.response?.data?.error || 'Failed to generate blog post');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateIdeas = async () => {
    if (!industry || !targetAudience) {
      alert('Please enter industry and target audience');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/blog-automation/content-ideas', {
        industry,
        target_audience: targetAudience,
        content_types: ['tutorial', 'listicle', 'analysis'],
        idea_count: 12
      });

      if (response.data.success) {
        setContentIdeas(response.data.content_ideas);
      }
    } catch (error) {
      console.error('Error generating ideas:', error);
      alert(error.response?.data?.error || 'Failed to generate content ideas');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectIdea = (idea) => {
    setTopic(idea.title);
    setContentType(idea.content_type);
    setKeywords(idea.suggested_keywords || []);
    setActiveTab('generate');
  };

  // Inline styles matching ProjectForm
  const tabStyle = (isActive) => ({
    padding: '12px 24px',
    background: isActive ? 'var(--primary-50)' : 'transparent',
    color: isActive ? 'var(--primary-600)' : 'var(--gray-600)',
    border: 'none',
    borderBottom: `2px solid ${isActive ? 'var(--primary-600)' : 'transparent'}`,
    fontWeight: isActive ? '600' : '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    marginBottom: '-2px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  });

  const tagStyle = {
    background: 'var(--primary-100)',
    color: 'var(--primary-700)',
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '13px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  };

  const tagRemoveStyle = {
    background: 'none',
    border: 'none',
    color: 'var(--primary-700)',
    cursor: 'pointer',
    fontWeight: 'bold',
    padding: 0,
    fontSize: '16px',
    lineHeight: 1
  };

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Blog Automation</span>
      </nav>

      {/* Main Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <DocumentTextIcon style={{ width: 28, height: 28 }} />
              AI Blog Automation
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Generate, schedule, and publish SEO-optimized blog content across multiple platforms
            </p>
          </div>
        </div>

        {/* Stats */}
        {analytics && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ background: 'var(--gray-50)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--gray-600)', fontSize: '14px', marginBottom: '8px' }}>
                <DocumentTextIcon style={{ width: 18, height: 18 }} />
                <span>Published Posts</span>
              </div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--primary-600)' }}>{analytics.published_posts}</div>
            </div>

            <div style={{ background: 'var(--gray-50)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--gray-600)', fontSize: '14px', marginBottom: '8px' }}>
                <ChartBarIcon style={{ width: 18, height: 18 }} />
                <span>Total Views</span>
              </div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--primary-600)' }}>{analytics.total_views.toLocaleString()}</div>
            </div>

            <div style={{ background: 'var(--gray-50)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--gray-600)', fontSize: '14px', marginBottom: '8px' }}>
                <ClockIcon style={{ width: 18, height: 18 }} />
                <span>Avg Reading Time</span>
              </div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--primary-600)' }}>{analytics.average_reading_time}</div>
              <div style={{ color: 'var(--gray-600)', fontSize: '14px' }}>minutes</div>
            </div>

            <div style={{ background: 'var(--gray-50)', borderRadius: '8px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--gray-600)', fontSize: '14px', marginBottom: '8px' }}>
                <RocketLaunchIcon style={{ width: 18, height: 18 }} />
                <span>Top Platform</span>
              </div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: 'var(--primary-600)' }}>{analytics.top_performing_platforms[0]?.platform}</div>
              <div style={{ color: 'var(--gray-600)', fontSize: '14px' }}>{analytics.top_performing_platforms[0]?.views} views</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '24px',
          borderBottom: '2px solid var(--gray-200)',
          paddingBottom: 0
        }}>
          <button style={tabStyle(activeTab === 'generate')} onClick={() => setActiveTab('generate')}>
            <SparklesIcon style={{ width: 18, height: 18 }} />
            Generate Content
          </button>
          <button style={tabStyle(activeTab === 'ideas')} onClick={() => setActiveTab('ideas')}>
            <PlusCircleIcon style={{ width: 18, height: 18 }} />
            Content Ideas
          </button>
          <button style={tabStyle(activeTab === 'platforms')} onClick={() => setActiveTab('platforms')}>
            <CalendarIcon style={{ width: 18, height: 18 }} />
            Publishing Platforms
          </button>
        </div>

        {/* Generate Content Tab */}
        {activeTab === 'generate' && (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '20px', color: 'var(--gray-900)' }}>Generate Blog Post</h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 600, color: 'var(--gray-700)', marginBottom: '8px', fontSize: '14px' }}>Topic *</label>
              <input
                type="text"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g., Digital Marketing Strategy"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--gray-300)',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <div style={{ marginTop: '4px', fontSize: '13px', color: 'var(--gray-500)' }}>Enter the main topic or title for your blog post</div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 600, color: 'var(--gray-700)', marginBottom: '8px', fontSize: '14px' }}>Content Type *</label>
              <select
                value={contentType}
                onChange={(e) => setContentType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--gray-300)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <option value="tutorial">Tutorial / How-To</option>
                <option value="listicle">Listicle</option>
                <option value="analysis">Analysis / Insights</option>
                <option value="review">Review</option>
                <option value="news">News / Updates</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 600, color: 'var(--gray-700)', marginBottom: '8px', fontSize: '14px' }}>Target Keywords *</label>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                alignItems: 'center',
                minHeight: '42px'
              }}>
                {keywords.map(keyword => (
                  <span key={keyword} style={tagStyle}>
                    {keyword}
                    <button style={tagRemoveStyle} onClick={() => handleRemoveKeyword(keyword)}>×</button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={handleAddKeyword}
                placeholder="Type keyword and press Enter"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--gray-300)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginTop: keywords.length > 0 ? '8px' : '0'
                }}
              />
              <div style={{ marginTop: '4px', fontSize: '13px', color: 'var(--gray-500)' }}>Add SEO keywords (press Enter to add each)</div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button className="btn btn-primary" onClick={handleGenerateBlogPost} disabled={loading}>
                {loading ? (
                  <span style={{
                    display: 'inline-block',
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '50%',
                    borderTopColor: 'white',
                    animation: 'spin 1s ease-in-out infinite'
                  }}></span>
                ) : (
                  <SparklesIcon style={{ width: 18, height: 18 }} />
                )}
                {loading ? 'Generating...' : 'Generate Blog Post'}
              </button>
            </div>

            {generatedPost && (
              <div style={{
                background: 'var(--gray-50)',
                border: '1px solid var(--gray-200)',
                borderRadius: '8px',
                padding: '20px',
                marginTop: '20px'
              }}>
                <h3 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--gray-900)', marginBottom: '16px' }}>{generatedPost.title}</h3>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '12px',
                  marginTop: '16px'
                }}>
                  <div style={{ background: 'white', padding: '12px', borderRadius: '6px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--gray-600)', fontWeight: 600, marginBottom: '4px' }}>Word Count</div>
                    <div style={{ fontSize: '14px', color: 'var(--gray-900)' }}>{generatedPost.word_count}</div>
                  </div>
                  <div style={{ background: 'white', padding: '12px', borderRadius: '6px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--gray-600)', fontWeight: 600, marginBottom: '4px' }}>Reading Time</div>
                    <div style={{ fontSize: '14px', color: 'var(--gray-900)' }}>{generatedPost.reading_time} min</div>
                  </div>
                  <div style={{ background: 'white', padding: '12px', borderRadius: '6px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--gray-600)', fontWeight: 600, marginBottom: '4px' }}>Content Type</div>
                    <div style={{ fontSize: '14px', color: 'var(--gray-900)' }}>{generatedPost.content_type}</div>
                  </div>
                  <div style={{ background: 'white', padding: '12px', borderRadius: '6px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--gray-600)', fontWeight: 600, marginBottom: '4px' }}>SEO Title</div>
                    <div style={{ fontSize: '14px', color: 'var(--gray-900)' }}>{generatedPost.seo_title}</div>
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <strong style={{ fontSize: 14, color: 'var(--gray-700)' }}>Excerpt:</strong>
                  <p style={{ marginTop: 8, color: 'var(--gray-600)', lineHeight: 1.6 }}>{generatedPost.excerpt}</p>
                </div>

                <div style={{ marginTop: 16 }}>
                  <strong style={{ fontSize: 14, color: 'var(--gray-700)' }}>Tags:</strong>
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {generatedPost.tags.map((tag) => (
                      <span key={tag} style={tagStyle}>{tag}</span>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <strong style={{ fontSize: 14, color: 'var(--gray-700)', marginBottom: 8, display: 'block' }}>Content Preview:</strong>
                  <div style={{
                    background: 'white',
                    padding: '16px',
                    borderRadius: '6px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6
                  }}>{generatedPost.content}</div>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                  <button className="btn btn-secondary">
                    <DocumentTextIcon style={{ width: 18, height: 18 }} />
                    Edit Content
                  </button>
                  <button className="btn btn-primary">
                    <RocketLaunchIcon style={{ width: 18, height: 18 }} />
                    Schedule Publishing
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Content Ideas Tab */}
        {activeTab === 'ideas' && (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '20px', color: 'var(--gray-900)' }}>Content Idea Generator</h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 600, color: 'var(--gray-700)', marginBottom: '8px', fontSize: '14px' }}>Industry *</label>
              <input
                type="text"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g., SaaS, E-commerce, Healthcare"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--gray-300)',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontWeight: 600, color: 'var(--gray-700)', marginBottom: '8px', fontSize: '14px' }}>Target Audience *</label>
              <input
                type="text"
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                placeholder="e.g., Small business owners, Marketing managers"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--gray-300)',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button className="btn btn-primary" onClick={handleGenerateIdeas} disabled={loading}>
                {loading ? (
                  <span style={{
                    display: 'inline-block',
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '50%',
                    borderTopColor: 'white',
                    animation: 'spin 1s ease-in-out infinite'
                  }}></span>
                ) : (
                  <SparklesIcon style={{ width: 18, height: 18 }} />
                )}
                {loading ? 'Generating...' : 'Generate Ideas'}
              </button>
            </div>

            {contentIdeas.length > 0 && (
              <>
                <h3 style={{ fontSize: 18, fontWeight: 600, marginTop: 32, marginBottom: 16, color: 'var(--gray-900)' }}>
                  Content Ideas ({contentIdeas.length})
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: '16px'
                }}>
                  {contentIdeas.map(idea => (
                    <div
                      key={idea.id}
                      onClick={() => handleSelectIdea(idea)}
                      style={{
                        background: 'white',
                        border: '1px solid var(--gray-200)',
                        borderRadius: '8px',
                        padding: '16px',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <h4 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--gray-900)', marginBottom: '8px' }}>{idea.title}</h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '12px' }}>
                        <span style={{ padding: '4px 8px', background: 'var(--gray-100)', borderRadius: '4px', color: 'var(--gray-700)' }}>{idea.content_type}</span>
                        <span style={{ padding: '4px 8px', background: 'var(--gray-100)', borderRadius: '4px', color: 'var(--gray-700)' }}>{idea.difficulty}</span>
                        <span style={{ padding: '4px 8px', background: 'var(--gray-100)', borderRadius: '4px', color: 'var(--gray-700)' }}>{idea.estimated_traffic_potential} Traffic</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Publishing Platforms Tab */}
        {activeTab === 'platforms' && (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '24px', color: 'var(--gray-900)' }}>Publishing Platforms</h3>

            <div style={{ display: 'grid', gap: '16px' }}>
              {platforms.map(platform => (
                <div
                  key={platform.id}
                  style={{
                    padding: '20px',
                    border: '1px solid var(--gray-200)',
                    borderRadius: '8px',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: '20px',
                    alignItems: 'start'
                  }}
                >
                  <div>
                    <h4 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: 'var(--gray-900)' }}>{platform.name}</h4>
                    <p style={{ color: 'var(--gray-600)', marginBottom: '12px' }}>{platform.description}</p>

                    <div style={{ marginBottom: '12px' }}>
                      <strong style={{ fontSize: '14px', color: 'var(--gray-700)' }}>Features:</strong>
                      <ul style={{ marginTop: '6px', paddingLeft: '20px', color: 'var(--gray-600)' }}>
                        {platform.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    </div>

                    <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: 'var(--gray-600)' }}>
                      <span><strong>Length:</strong> {platform.optimal_content_length}</span>
                      <span><strong>Frequency:</strong> {platform.posting_frequency}</span>
                    </div>
                  </div>

                  <button className="btn btn-secondary">
                    <CheckCircleIcon style={{ width: 18, height: 18 }} />
                    Configure
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogAutomation;
